import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../components/auth/auth.selector";
import ProfileBanner from "../../components-v2/profile-menu/ProfileBanner";
import ProfileInfoCard, {
  ProfileField,
} from "../../components-v2/profile-menu/ProfileInfoCard";
import {
  PersonIcon,
  BriefcaseIcon,
  LocationIcon,
  GlobeIcon,
  EmailIcon,
  PhoneIcon,
  CalendarIcon,
  BuildingIcon,
  LinkedInIcon,
  TwitterIcon,
  WebsiteIcon,
  SaveIcon,
  CloseIcon,
} from "../../components-v2/profile-menu/ProfileIcons";
import "../../styles-v2/profile-menu/v2-profile-page.css";

const ACCENT = "#eb1414";

const initialFormState = (user) => ({
  firstName: user?.name?.split(" ")[0] || "John",
  lastName: user?.name?.split(" ").slice(1).join(" ") || "Doe",
  email: user?.email || "john@wtfgyms.com",
  phone: user?.phone || "+91 98765 43210",
  dob: user?.dob || "15/05/1990",
  role: user?.role || "Admin",
  department: user?.department || "Operations",
  employeeId: user?.employeeId || "WTF-001",
  reportingManager: user?.reportingManager || "Sarah Wilson",
  streetAddress: user?.streetAddress || "123 Fitness Lane",
  city: user?.city || "Mumbai",
  state: user?.state || "Maharashtra",
  pincode: user?.pincode || "400001",
  linkedin: user?.linkedin || "linkedin.com/in/johndoe",
  twitter: user?.twitter || "@johndoe",
  website: user?.website || "johndoe.com",
});

const ProfilePage = () => {
  const userInfoData = useSelector(selectUser);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(initialFormState(userInfoData));

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setFormData(initialFormState(userInfoData));
    setIsEditing(false);
  };

  const handleSave = () => {
    // API call to save profile data would go here
    setIsEditing(false);
  };

  const handleImageChange = (file) => {
    // API call to upload profile image would go here
    console.log("Profile image selected:", file.name);
  };

  return (
    <div className="v2-prof-pg">
      {/* Page Header */}
      <div className="v2-prof-pg__header">
        <div className="v2-prof-pg__header-text">
          <h1 className="v2-prof-pg__title">Profile</h1>
          <p className="v2-prof-pg__subtitle">
            Manage your personal information and preferences
          </p>
        </div>
        <div className="v2-prof-pg__header-actions">
          {isEditing ? (
            <>
              <button
                className="v2-prof-pg__btn v2-prof-pg__btn--cancel"
                onClick={handleCancel}
              >
                <CloseIcon size={16} />
                <span>Cancel</span>
              </button>
              <button
                className="v2-prof-pg__btn v2-prof-pg__btn--save"
                onClick={handleSave}
              >
                <SaveIcon size={16} color="#ffffff" />
                <span>Save Changes</span>
              </button>
            </>
          ) : (
            <button
              className="v2-prof-pg__btn v2-prof-pg__btn--edit"
              onClick={handleEdit}
            >
              <span>Edit Profile</span>
            </button>
          )}
        </div>
      </div>

      {/* Profile Banner */}
      <ProfileBanner
        userData={userInfoData}
        isEditing={isEditing}
        onImageChange={handleImageChange}
      />

      {/* Info Cards Grid */}
      <div className="v2-prof-pg__grid">
        {/* Personal Information */}
        <ProfileInfoCard
          icon={<PersonIcon size={22} color={ACCENT} />}
          title="Personal Information"
          subtitle="Your basic personal details"
        >
          <div className="v2-prof-pg__field-row">
            <ProfileField
              label="First Name"
              value={formData.firstName}
              name="firstName"
              isEditing={isEditing}
              onChange={handleChange}
            />
            <ProfileField
              label="Last Name"
              value={formData.lastName}
              name="lastName"
              isEditing={isEditing}
              onChange={handleChange}
            />
          </div>
          <ProfileField
            label="Email Address"
            icon={<EmailIcon size={16} color={ACCENT} />}
            value={formData.email}
            name="email"
            type="email"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Phone Number"
            icon={<PhoneIcon size={16} color={ACCENT} />}
            value={formData.phone}
            name="phone"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Date of Birth"
            icon={<CalendarIcon size={16} color={ACCENT} />}
            value={formData.dob}
            name="dob"
            isEditing={isEditing}
            onChange={handleChange}
          />
        </ProfileInfoCard>

        {/* Professional Details */}
        <ProfileInfoCard
          icon={<BriefcaseIcon size={22} color={ACCENT} />}
          title="Professional Details"
          subtitle="Your work-related information"
        >
          <ProfileField
            label="Role"
            value={formData.role}
            name="role"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Department"
            icon={<BuildingIcon size={16} color={ACCENT} />}
            value={formData.department}
            name="department"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Employee ID"
            value={formData.employeeId}
            name="employeeId"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Reporting Manager"
            value={formData.reportingManager}
            name="reportingManager"
            isEditing={isEditing}
            onChange={handleChange}
          />
        </ProfileInfoCard>

        {/* Address */}
        <ProfileInfoCard
          icon={<LocationIcon size={22} color={ACCENT} />}
          title="Address"
          subtitle="Your residential address"
        >
          <ProfileField
            label="Street Address"
            value={formData.streetAddress}
            name="streetAddress"
            isEditing={isEditing}
            onChange={handleChange}
            isTextarea
          />
          <div className="v2-prof-pg__field-row">
            <ProfileField
              label="City"
              value={formData.city}
              name="city"
              isEditing={isEditing}
              onChange={handleChange}
            />
            <ProfileField
              label="State"
              value={formData.state}
              name="state"
              isEditing={isEditing}
              onChange={handleChange}
            />
          </div>
          <ProfileField
            label="Pincode"
            value={formData.pincode}
            name="pincode"
            isEditing={isEditing}
            onChange={handleChange}
          />
        </ProfileInfoCard>

        {/* Social Links */}
        <ProfileInfoCard
          icon={<GlobeIcon size={22} color={ACCENT} />}
          title="Social Links"
          subtitle="Your social media profiles"
        >
          <ProfileField
            label="LinkedIn"
            icon={<LinkedInIcon size={16} color={ACCENT} />}
            value={formData.linkedin}
            name="linkedin"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Twitter"
            icon={<TwitterIcon size={16} color={ACCENT} />}
            value={formData.twitter}
            name="twitter"
            isEditing={isEditing}
            onChange={handleChange}
          />
          <ProfileField
            label="Website"
            icon={<WebsiteIcon size={16} color={ACCENT} />}
            value={formData.website}
            name="website"
            isEditing={isEditing}
            onChange={handleChange}
          />
        </ProfileInfoCard>
      </div>
    </div>
  );
};

export default ProfilePage;
